import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform,
  useWindowDimensions,
  Animated,
  ActivityIndicator,
  KeyboardAvoidingView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuthStore, useChatStore, useCoworkerStore, useModalStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import {
  PROFESSIONAL_ROLES,
  PRICING_PLANS,
  PLUS_MONTHLY_CREDITS_DISPLAY,
} from '@/constants/config';
import {
  MODE_CARDS,
  ONBOARDING_GOALS,
  ONBOARDING_VARIANT,
  CHAT_MODE_COLOR,
  WORK_MODE_COLOR,
  BRAND_GREEN,
  BRAND_GREEN_HOVER,
  BRAND_GREEN_DEEP,
  IN_HOUSE_MODEL_NAME,
  getRolePlaybook,
  getOnboardingDemos,
} from '@/constants/onboarding';
import { trackEvent } from '@/constants/utils/analytics';
import socketManager from '@/lib/socket';
import { startWorkFromPrompt } from '@/lib/startWorkFromPrompt';
import useResponsiveness from '@/hooks/useResponsiveness';
import OnboardingNameInput from './shared/OnboardingNameInput';
import WorkDemoCard from './shared/WorkDemoCard';
import ChatDemoCard from './shared/ChatDemoCard';
import ModeCard from './shared/ModeCard';

const STEPS = [
  { id: 'welcome', desktopWidth: 460 },
  { id: 'about_you', desktopWidth: 560 },
  { id: 'chat_vs_work', desktopWidth: 760 },
  { id: 'plus_and_launch', desktopWidth: 580 },
];

const MOBILE_BREAKPOINT = 768;

export default function OnboardingFlow({ onComplete, userName, recordStep, renderNativeFinish }) {
  const { colors, theme } = useTheme();
  const insets = useSafeAreaInsets();
  const isDark = isDarkTheme(theme);
  const { width: windowWidth } = useWindowDimensions();
  const { isXS } = useResponsiveness();
  const router = useRouter();

  const updateProfile = useAuthStore((s) => s.updateProfile);
  const userId = useAuthStore((s) => s.user?.id);
  const isPlus = useAuthStore((s) => !!s.user?.flags?.isPlus);
  const userEmail = useAuthStore((s) => s.user?.email);
  const isEmailVerified = useAuthStore((s) => !!s.user?.flags?.isEmailVerified);
  const pendingVerificationCredits = useAuthStore((s) => Number(s.user?.credits?.pendingVerificationCredits) || 0);
  const currentCredits = useAuthStore((s) => Number(s.user?.credits?.totalAvailable) || 0);
  const createConversation = useChatStore((s) => s.createConversation);
  const setComposerMode = useCoworkerStore((s) => s.setComposerMode);

  const [step, setStep] = useState(0);
  const [name, setName] = useState(userName || '');
  const [nameError, setNameError] = useState(false);
  const [role, setRole] = useState(null);
  const [showAllRoles, setShowAllRoles] = useState(false);
  const [goals, setGoals] = useState([]);
  const [billingAnnual, setBillingAnnual] = useState(true);
  const [launchingId, setLaunchingId] = useState(null);

  const fade = useRef(new Animated.Value(0)).current;
  const slide = useRef(new Animated.Value(14)).current;
  const shake = useRef(new Animated.Value(0)).current;
  const scrollRef = useRef(null);

  const isMobile = Platform.OS !== 'web' || windowWidth < MOBILE_BREAKPOINT;
  const isLastStep = step === STEPS.length - 1;
  const firstName = name.trim().split(/\s+/)[0] || '';
  const playbook = useMemo(() => getRolePlaybook(role), [role]);
  const demos = useMemo(() => getOnboardingDemos(role, goals), [role, goals]);
  const visibleRoles = useMemo(() => {
    if (showAllRoles) return PROFESSIONAL_ROLES;
    const featured = PROFESSIONAL_ROLES.filter((item) => item.featured);
    const selectedExtra = PROFESSIONAL_ROLES.find((item) => item.value === role && !item.featured);
    if (!selectedExtra) return featured;
    const withoutOther = featured.filter((item) => item.value !== 'other');
    const other = featured.find((item) => item.value === 'other');
    return [...withoutOther, selectedExtra, other].filter(Boolean);
  }, [showAllRoles, role]);
  const hiddenRoleCount = PROFESSIONAL_ROLES.filter((item) => !item.featured && item.value !== role).length;

  useEffect(() => {
    fade.setValue(0);
    slide.setValue(14);
    Animated.parallel([
      Animated.timing(fade, { toValue: 1, duration: 320, useNativeDriver: Platform.OS !== 'web' }),
      Animated.timing(slide, { toValue: 0, duration: 320, useNativeDriver: Platform.OS !== 'web' }),
    ]).start();
    scrollRef.current?.scrollTo?.({ y: 0, animated: false });
    recordStep?.(step);
    trackEvent('onboarding', 'step_viewed', STEPS[step].id, step);
  }, [step]);

  const triggerShake = useCallback(() => {
    shake.setValue(0);
    Animated.sequence(
      [10, -10, 8, -8, 0].map((toValue) =>
        Animated.timing(shake, { toValue, duration: 50, useNativeDriver: Platform.OS !== 'web' })
      )
    ).start();
  }, [shake]);

  const goTo = useCallback((next) => {
    setStep(Math.max(0, Math.min(STEPS.length - 1, next)));
  }, []);

  const handleContinue = useCallback(() => {
    if (step === 0) {
      if (!name.trim()) {
        setNameError(true);
        triggerShake();
        return;
      }
      updateProfile({ fullName: name.trim() }).catch(() => {});
      trackEvent('onboarding', 'name_completed', ONBOARDING_VARIANT);
    }
    if (step === 1) {
      const profile = {};
      if (role) profile.professionalRole = role;
      if (goals.length) profile.interestedFeatures = goals;
      if (Object.keys(profile).length) updateProfile(profile).catch(() => {});
      trackEvent('onboarding', 'about_you_completed', ONBOARDING_VARIANT, { role, goals });
    }
    if (step === 2) {
      trackEvent('onboarding', 'modes_viewed', ONBOARDING_VARIANT);
    }
    goTo(step + 1);
  }, [step, name, role, goals, updateProfile, goTo, triggerShake]);

  const handleUpgrade = useCallback(() => {
    trackEvent('onboarding', 'upgrade_tapped', ONBOARDING_VARIANT, { role, billing: billingAnnual ? 'annual' : 'monthly' });
    setComposerMode('chat');
    onComplete?.();
    useModalStore.getState().actions.openModal('PricingModal', {
      title: 'Unlock Work mode with Plus',
      subtitle: 'An AI agent with its own computer that plans, researches, builds files, and keeps going until the job is done.',
      initialCadence: billingAnnual ? 'annual' : 'monthly',
    });
  }, [onComplete, role, billingAnnual, setComposerMode]);

  const launchConversation = useCallback(async (prompt) => {
    const conversation = await createConversation({ userId, formattedMessage: { text: prompt } });
    if (!conversation) throw new Error('No conversation');
    setTimeout(() => socketManager.emitKickConversation(userId, conversation.id), 400);
    setTimeout(() => router.push(`/c/${conversation.id}`), 150);
  }, [createConversation, userId, router]);

  const handleLaunchChat = useCallback(async (prompt, index) => {
    if (launchingId) return;
    setLaunchingId(`chat-${index}`);
    trackEvent('onboarding', 'launch_chat', ONBOARDING_VARIANT, { role, index });
    onComplete?.();
    try {
      await launchConversation(prompt);
    } catch {
      router.push('/app');
    }
  }, [launchingId, onComplete, launchConversation, router, role]);

  const handleLaunchWork = useCallback(async (example, index) => {
    if (launchingId) return;
    setLaunchingId(`work-${index}`);
    trackEvent('onboarding', 'launch_work', ONBOARDING_VARIANT, { role, index });
    onComplete?.();
    setComposerMode('coworker');
    const created = await startWorkFromPrompt({ prompt: example.prompt, router, source: 'onboarding' });
    if (!created) {
      setLaunchingId(null);
      router.push('/app');
    }
  }, [launchingId, onComplete, setComposerMode, router, role]);

  const handleTryChatFree = useCallback(() => {
    trackEvent('onboarding', 'try_chat_free', ONBOARDING_VARIANT, { role });
    onComplete?.();
    setComposerMode('chat');
    router.push('/app');
  }, [onComplete, setComposerMode, router, role]);

  const handleExploreOnMyOwn = useCallback(() => {
    trackEvent('onboarding', 'finish_explore', ONBOARDING_VARIANT, { role });
    setComposerMode('chat');
    onComplete?.();
  }, [onComplete, role, setComposerMode]);

  // Unverified free accounts hold a smaller starting grant until they click
  // the email link; surface the bonus as a reward, not a gate.
  const showVerifyBonus = !isPlus && !isEmailVerified && pendingVerificationCredits > 0;
  const needsEmailVerification = !isEmailVerified && !!userEmail;
  const creditsNowDisplay = currentCredits.toLocaleString();
  const welcomeCreditNote = currentCredits > 0
    ? `${creditsNowDisplay} credits are ready for you.${showVerifyBonus ? ` Verify your email to add ${pendingVerificationCredits.toLocaleString()} more.` : needsEmailVerification ? ' Verify your email to start chatting.' : ''}`
    : needsEmailVerification
      ? 'One quick step before your first chat: verify your email. No credit card needed.'
      : 'Start with a question or explore what you can make with Gab AI.';

  const handleClaimBonus = useCallback(() => {
    trackEvent('onboarding', 'claim_verify_bonus', ONBOARDING_VARIANT, { role });
    onComplete?.();
    setComposerMode('chat');
    router.push('/app');
    // Let the overlay finish dismissing before the reward modal springs in.
    setTimeout(() => {
      useModalStore.getState().actions.openModal('VerifyEmailModal', { source: 'onboarding_finish' });
    }, 350);
  }, [onComplete, setComposerMode, router, role]);

  const surface = {
    subtle: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.025)',
    subtleHover: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.045)',
    border: isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)',
  };

  // ---- Step 0: Welcome --------------------------------------------------

  const renderWelcome = () => (
    <View style={styles.centered}>
      <View style={styles.logoRow}>
        <Logo width={32} height={32} color={colors.text_color_primary} />
        <Text weight="bold" style={styles.logoText}>Gab AI</Text>
      </View>

      <Text weight="bold" style={[styles.heroTitle, isXS && { fontSize: 26, lineHeight: 32 }]}>
        Welcome. Let's get you set up.
      </Text>
      <Text size="s" color="secondary" style={styles.heroSubtitle}>
        Every top AI model in one place — plus Work, an AI agent with its own computer that finishes real jobs for you.
      </Text>

      <View style={styles.teaserRow}>
        {[MODE_CARDS.chat, MODE_CARDS.work].map((mode) => (
          <View
            key={mode.id}
            style={[styles.teaserPill, {
              backgroundColor: isDark ? `${mode.color}14` : `${mode.color}0C`,
              borderColor: isDark ? `${mode.color}40` : `${mode.color}33`,
            }]}
          >
            <View style={[styles.teaserIcon, { backgroundColor: mode.color }]}>
              <Icon name={mode.icon} size={12} color="#fff" solid />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text size="s" weight="bold">{mode.title}</Text>
              <Text size="xxs" color="secondary" numberOfLines={2} style={{ lineHeight: 14 }}>
                {mode.id === 'chat' ? 'Instant answers and creation' : 'Hands you finished work'}
              </Text>
            </View>
          </View>
        ))}
      </View>

      <Animated.View style={[styles.nameSection, { transform: [{ translateX: shake }] }]}>
        <Text size="s" weight="semibold" style={{ marginBottom: 6 }}>First, what should we call you?</Text>
        <OnboardingNameInput
          value={name}
          onChangeText={(text) => { setName(text); if (nameError && text.trim()) setNameError(false); }}
          placeholder="Your first name"
          autoFocus={!isMobile}
        />
        {nameError ? (
          <View style={styles.inlineNote}>
            <Icon name="circle-exclamation" size={11} color="#EF4444" solid />
            <Text size="xxs" weight="medium" style={{ color: '#EF4444' }}>
              Add your name so Gab can personalize everything for you.
            </Text>
          </View>
        ) : (
          <View style={styles.inlineNote}>
            <Icon name="gift" size={11} color="#10B981" solid />
            <Text size="xxs" color="tertiary">
              {welcomeCreditNote}
            </Text>
          </View>
        )}
      </Animated.View>
    </View>
  );

  // ---- Step 1: About you ------------------------------------------------

  const toggleGoal = (value) => {
    setGoals((prev) => (prev.includes(value) ? prev.filter((g) => g !== value) : [...prev, value]));
  };

  const renderAboutYou = () => (
    <View style={styles.centered}>
      <Text weight="bold" style={styles.sectionTitle}>
        {firstName ? `Nice to meet you, ${firstName}.` : 'Tell us a little about you.'}
      </Text>
      <Text size="s" color="secondary" style={styles.sectionSubtitle}>
        Two quick picks. We'll use them to suggest what to do first.
      </Text>

      <View style={styles.fieldBlock}>
        <Text size="xs" weight="bold" color="tertiary" style={styles.fieldLabel}>WHAT DO YOU DO?</Text>
        <View style={[styles.chipGrid, { gap: isXS ? 8 : 6 }]}>
          {visibleRoles.map((item) => {
            const selected = role === item.value;
            return (
              <Pressable
                key={item.value}
                onPress={() => setRole(selected ? null : item.value)}
                style={({ hovered }) => [
                  styles.chip,
                  {
                    backgroundColor: selected
                      ? (isDark ? 'rgba(31,107,71,0.22)' : 'rgba(31,107,71,0.10)')
                      : hovered ? surface.subtleHover : surface.subtle,
                    borderColor: selected ? BRAND_GREEN : surface.border,
                  },
                ]}
              >
                <Icon name={selected ? 'check' : item.icon} size={12} color={selected ? BRAND_GREEN : colors.text_color_tertiary} solid={selected} />
                <Text size="s" weight={selected ? 'semibold' : 'medium'}>{item.label}</Text>
              </Pressable>
            );
          })}
          <Pressable
            onPress={() => setShowAllRoles((open) => !open)}
            style={({ hovered }) => [
              styles.chip,
              {
                backgroundColor: hovered ? surface.subtleHover : surface.subtle,
                borderColor: surface.border,
              },
            ]}
          >
            <Icon name={showAllRoles ? 'chevron-up' : 'ellipsis'} size={12} color={colors.text_color_tertiary} />
            <Text size="s" weight="medium" color="secondary">
              {showAllRoles ? 'Show less' : `More roles (${hiddenRoleCount})`}
            </Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.fieldBlock}>
        <Text size="xs" weight="bold" color="tertiary" style={styles.fieldLabel}>WHAT DO YOU WANT TO GET DONE?</Text>
        <View style={styles.goalGrid}>
          {ONBOARDING_GOALS.map((goal) => {
            const selected = goals.includes(goal.value);
            return (
              <Pressable
                key={goal.value}
                onPress={() => toggleGoal(goal.value)}
                style={({ hovered }) => [
                  styles.goalTile,
                  { width: isXS ? '48%' : '23.5%' },
                  {
                    backgroundColor: selected
                      ? (isDark ? `${goal.color}1F` : `${goal.color}12`)
                      : hovered ? surface.subtleHover : surface.subtle,
                    borderColor: selected ? goal.color : surface.border,
                  },
                ]}
              >
                {selected && (
                  <View style={[styles.goalCheck, { backgroundColor: goal.color }]}>
                    <Icon name="check" size={8} color="#fff" solid />
                  </View>
                )}
                <View style={[styles.goalIcon, { backgroundColor: `${goal.color}1A` }]}>
                  <Icon name={goal.icon} size={15} color={goal.color} solid />
                </View>
                <Text size="xs" weight={selected ? 'bold' : 'semibold'} style={{ textAlign: 'center' }}>
                  {goal.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
        <Text size="xxs" color="tertiary" style={{ marginTop: 10, textAlign: 'center' }}>
          {goals.length > 0 ? `${goals.length} selected — you can change this anytime.` : 'Pick as many as you like, or continue without picking.'}
        </Text>
      </View>
    </View>
  );

  // ---- Step 2: Chat vs Work ---------------------------------------------

  const renderModes = () => (
    <View style={styles.centered}>
      <Text weight="bold" style={styles.sectionTitle}>Two ways to get things done.</Text>
      <Text size="s" color="secondary" style={styles.sectionSubtitle}>
        Chat is instant. Work finishes the job.
      </Text>

      <View style={[styles.modeRow, isMobile && styles.modeColumn]}>
        <ModeCard mode={MODE_CARDS.chat} compact={isMobile}>
          <ChatDemoCard compact={isMobile} demo={demos.chat} />
        </ModeCard>
        <ModeCard mode={MODE_CARDS.work} compact={isMobile}>
          <WorkDemoCard compact={isMobile} demo={demos.work} />
        </ModeCard>
      </View>

      <View style={[styles.ruleStrip, { backgroundColor: surface.subtle, borderColor: surface.border }]}>
        <Text size="xs" color="secondary" style={{ textAlign: 'center', lineHeight: 18, flex: 1 }}>
          Try Chat with your starting credits. Plus includes Work and credit-free chats with {IN_HOUSE_MODEL_NAME}.
        </Text>
      </View>
    </View>
  );

  // ---- Step 3: Plus + first action ---------------------------------------

  const displayPrice = billingAnnual ? PRICING_PLANS.plus.annualMonthlyPrice : PRICING_PLANS.plus.monthlyPrice;

  const renderWorkExample = (example, index, { launchable, locked = false }) => {
    const isLaunching = launchingId === `work-${index}`;
    const rowStyle = (hovered) => [
      styles.exampleRow,
      {
        backgroundColor: launchable && hovered
          ? (isDark ? `${WORK_MODE_COLOR}1C` : `${WORK_MODE_COLOR}12`)
          : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)'),
        borderColor: launchable && hovered ? `${WORK_MODE_COLOR}66` : (isDark ? `${WORK_MODE_COLOR}30` : `${WORK_MODE_COLOR}26`),
      },
      launchable && Platform.OS === 'web' && { cursor: 'pointer' },
    ];
    const inner = (
      <>
        <View style={[styles.exampleIcon, { backgroundColor: `${WORK_MODE_COLOR}1F` }]}>
          {isLaunching ? (
            <ActivityIndicator size="small" color={WORK_MODE_COLOR} />
          ) : (
            <Icon name={example.icon} size={13} color={WORK_MODE_COLOR} solid />
          )}
        </View>
        <Text size="xs" weight="medium" style={{ flex: 1, lineHeight: 17 }}>{example.text}</Text>
        {launchable && <Icon name="arrow-right" size={11} color={WORK_MODE_COLOR} />}
        {locked && <Icon name="lock" size={11} color={colors.text_color_tertiary} solid />}
      </>
    );
    if (!launchable) {
      return <View key={example.text} style={rowStyle(false)}>{inner}</View>;
    }
    return (
      <Pressable
        key={example.text}
        onPress={() => handleLaunchWork(example, index)}
        disabled={!!launchingId}
        style={({ hovered }) => rowStyle(hovered)}
      >
        {inner}
      </Pressable>
    );
  };

  const renderChatStarters = ({ heading, subheading }) => (
    <View style={{ width: '100%' }}>
      <View style={styles.dividerRow}>
        <View style={[styles.dividerLine, { backgroundColor: surface.border }]} />
        <Text size="xxs" weight="bold" color="tertiary" style={{ letterSpacing: 0.8 }}>{heading}</Text>
        <View style={[styles.dividerLine, { backgroundColor: surface.border }]} />
      </View>
      {subheading && (
        <Text size="xs" color="secondary" style={{ textAlign: 'center', marginBottom: 10, lineHeight: 17 }}>{subheading}</Text>
      )}
      <View style={{ gap: 6 }}>
        {playbook.chat.map((prompt, index) => {
          const isLaunching = launchingId === `chat-${index}`;
          return (
            <Pressable
              key={prompt}
              onPress={() => handleLaunchChat(prompt, index)}
              disabled={!!launchingId}
              style={({ hovered }) => [
                styles.starterRow,
                {
                  backgroundColor: hovered ? (isDark ? 'rgba(31,107,71,0.16)' : 'rgba(31,107,71,0.08)') : surface.subtle,
                  borderColor: hovered ? `${CHAT_MODE_COLOR}66` : surface.border,
                },
              ]}
            >
              <View style={[styles.exampleIcon, { backgroundColor: `${CHAT_MODE_COLOR}1A` }]}>
                {isLaunching ? (
                  <ActivityIndicator size="small" color={CHAT_MODE_COLOR} />
                ) : (
                  <Icon name="comment" size={12} color={CHAT_MODE_COLOR} solid />
                )}
              </View>
              <Text size="xs" weight="medium" style={{ flex: 1, lineHeight: 17 }}>{prompt}</Text>
              <Icon name="arrow-right" size={11} color={colors.text_color_tertiary} />
            </Pressable>
          );
        })}
      </View>
    </View>
  );

  const renderFinishFree = () => (
    <View style={styles.centered}>
      <Text weight="bold" style={styles.sectionTitle}>
        {firstName ? `${firstName}, Work mode is where Gab AI shines.` : 'Work mode is where Gab AI shines.'}
      </Text>
      <Text size="s" color="secondary" style={styles.sectionSubtitle}>
        {playbook.headline}. It's our best feature for actually getting things done — and it's included with Plus.
      </Text>

      <View style={[styles.plusCard, {
        backgroundColor: isDark ? `${WORK_MODE_COLOR}12` : `${WORK_MODE_COLOR}08`,
        borderColor: isDark ? `${WORK_MODE_COLOR}45` : `${WORK_MODE_COLOR}38`,
      }]}>
        <View style={styles.plusHeader}>
          <View style={[styles.plusBadge, { backgroundColor: WORK_MODE_COLOR }]}>
            <Icon name="bolt" size={10} color="#fff" solid />
            <Text size="xxs" weight="bold" style={{ color: '#fff', letterSpacing: 0.5 }}>WORK MODE · PLUS ONLY</Text>
          </View>
          <Text size="xxs" color="tertiary">Cancel anytime</Text>
        </View>

        <Text size="xs" color="secondary" style={{ marginTop: 10, lineHeight: 17 }}>
          Jobs Work can finish for you the moment you upgrade:
        </Text>
        <View style={{ gap: 6, marginTop: 8 }}>
          {playbook.work.map((example, index) => renderWorkExample(example, index, { launchable: false, locked: true }))}
        </View>

        <View style={styles.perkRow}>
          {[
            { icon: 'laptop', text: 'Work mode with its own computer' },
            { icon: 'coins', text: `${PLUS_MONTHLY_CREDITS_DISPLAY} credits every month` },
            { icon: 'microphone', text: 'Voice mode' },
            { icon: 'layer-group', text: 'Every premium model' },
          ].map((perk) => (
            <View key={perk.text} style={styles.perkItem}>
              <Icon name={perk.icon} size={10} color={WORK_MODE_COLOR} solid />
              <Text size="xxs" color="secondary">{perk.text}</Text>
            </View>
          ))}
        </View>

        <View style={styles.priceRow}>
          <View style={[styles.billingToggle, { backgroundColor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)' }]}>
            {[{ id: 'monthly', label: 'Monthly' }, { id: 'annual', label: 'Annual' }].map((option) => {
              const active = option.id === 'annual' ? billingAnnual : !billingAnnual;
              return (
                <Pressable
                  key={option.id}
                  onPress={() => setBillingAnnual(option.id === 'annual')}
                  style={[styles.billingOption, active && { backgroundColor: isDark ? 'rgba(255,255,255,0.12)' : '#fff' }]}
                >
                  <Text size="xxs" weight={active ? 'bold' : 'medium'} color={active ? undefined : 'tertiary'}>{option.label}</Text>
                  {option.id === 'annual' && (
                    <View style={styles.saveBadge}>
                      <Text weight="bold" style={{ color: '#111', fontSize: 8 }}>-17%</Text>
                    </View>
                  )}
                </Pressable>
              );
            })}
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 3 }}>
              <Text weight="bold" style={{ fontSize: 28, letterSpacing: -1 }}>${displayPrice}</Text>
              <Text size="xs" color="secondary">/mo</Text>
            </View>
            {billingAnnual && (
              <Text size="xxs" color="tertiary">Billed ${PRICING_PLANS.plus.annualPrice}/year</Text>
            )}
          </View>
        </View>

        <Pressable
          onPress={handleUpgrade}
          style={({ hovered, pressed }) => [
            styles.primaryBtn,
            { backgroundColor: hovered ? BRAND_GREEN_HOVER : BRAND_GREEN_DEEP, marginTop: 12 },
            pressed && { opacity: 0.9 },
          ]}
        >
          <Icon name="bolt" size={13} color="#fff" solid />
          <Text size="n" weight="bold" style={{ color: '#fff' }}>Unlock Plus and start Working</Text>
        </Pressable>
      </View>

      <View style={[styles.tryChatCard, { backgroundColor: surface.subtle, borderColor: surface.border }]}>
        <View style={styles.tryChatHeader}>
          <View style={[styles.exampleIcon, { backgroundColor: `${CHAT_MODE_COLOR}1A` }]}>
            <Icon name="comments" size={13} color={CHAT_MODE_COLOR} solid />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text size="s" weight="bold">Start with a conversation.</Text>
            <Text size="xs" color="secondary" style={{ lineHeight: 17, marginTop: 2 }}>
              {currentCredits > 0
                ? `You have ${creditsNowDisplay} credits to explore Chat. Ask ${IN_HOUSE_MODEL_NAME} to plan your week, polish a draft, or talk through an idea.`
                : 'Ask a question, polish a draft, or talk through an idea. Verify your email to get your account ready for its first conversation.'}
            </Text>
          </View>
        </View>

        {showVerifyBonus && (
          <Pressable
            onPress={handleClaimBonus}
            style={({ hovered, pressed }) => [
              styles.bonusStrip,
              {
                backgroundColor: hovered ? (isDark ? 'rgba(16,185,129,0.16)' : 'rgba(16,185,129,0.12)') : (isDark ? 'rgba(16,185,129,0.10)' : 'rgba(16,185,129,0.07)'),
                borderColor: isDark ? 'rgba(16,185,129,0.35)' : 'rgba(16,185,129,0.28)',
              },
              pressed && { opacity: 0.9 },
            ]}
          >
            <View style={styles.bonusBadge}>
              <Icon name="coins" size={10} color="#fff" solid />
              <Text size="xxs" weight="bold" style={{ color: '#fff' }}>+{pendingVerificationCredits}</Text>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text size="xs" weight="bold">Bonus credits waiting for you</Text>
              <Text size="xxs" color="secondary" numberOfLines={2} style={{ lineHeight: 15 }}>
                {userEmail ? `We sent a link to ${userEmail}. ` : 'We sent a link to your inbox. '}Verify to start chatting and add your bonus.
              </Text>
            </View>
            <Text size="xs" weight="semibold" style={{ color: '#10B981' }}>Claim</Text>
            <Icon name="arrow-right" size={11} color="#10B981" />
          </Pressable>
        )}

        <Pressable
          onPress={needsEmailVerification ? handleClaimBonus : handleTryChatFree}
          accessibilityRole="button"
          style={({ hovered, pressed }) => [
            styles.secondaryBtn,
            {
              backgroundColor: hovered ? (isDark ? 'rgba(31,107,71,0.16)' : 'rgba(31,107,71,0.10)') : 'transparent',
              borderColor: `${CHAT_MODE_COLOR}80`,
            },
            pressed && { opacity: 0.9 },
          ]}
        >
          <Text size="s" weight="semibold" style={{ color: CHAT_MODE_COLOR }}>{needsEmailVerification ? 'Verify email and start chatting' : `Try Chat with ${IN_HOUSE_MODEL_NAME}`}</Text>
          <Icon name="arrow-right" size={12} color={CHAT_MODE_COLOR} />
        </Pressable>
      </View>
    </View>
  );

  const renderFinishPlus = () => (
    <View style={styles.centered}>
      <View style={[styles.plusBadge, { backgroundColor: WORK_MODE_COLOR, alignSelf: 'center', marginBottom: 12 }]}>
        <Icon name="bolt" size={10} color="#fff" solid />
        <Text size="xxs" weight="bold" style={{ color: '#fff', letterSpacing: 0.5 }}>PLUS ACTIVE</Text>
      </View>
      <Text weight="bold" style={styles.sectionTitle}>
        {firstName ? `You're all set, ${firstName}. Hand Work its first job.` : 'You’re all set. Hand Work its first job.'}
      </Text>
      <Text size="s" color="secondary" style={styles.sectionSubtitle}>
        {playbook.headline}. Tap one and watch Work boot up its computer and get started.
      </Text>

      <View style={{ gap: 6, width: '100%' }}>
        {playbook.work.map((example, index) => renderWorkExample(example, index, { launchable: true }))}
      </View>

      {renderChatStarters({ heading: 'OR ASK SOMETHING QUICK IN CHAT' })}

      <Pressable
        onPress={handleExploreOnMyOwn}
        style={({ hovered }) => [styles.ghostBtn, hovered && { backgroundColor: surface.subtle }]}
      >
        <Text size="xs" color="tertiary">I'll explore on my own</Text>
      </Pressable>
    </View>
  );

  const renderStep = () => {
    switch (step) {
      case 0: return renderWelcome();
      case 1: return renderAboutYou();
      case 2: return renderModes();
      default: return isPlus ? renderFinishPlus() : renderFinishFree();
    }
  };

  // ---- Shell --------------------------------------------------------------

  const header = (
    <View style={styles.header}>
      <View style={styles.progress}>
        {STEPS.map((s, index) => (
          <Pressable
            key={s.id}
            disabled={index >= step}
            onPress={() => goTo(index)}
            style={[styles.progressSegment, {
              backgroundColor: index <= step
                ? BRAND_GREEN
                : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'),
            }]}
          />
        ))}
      </View>
    </View>
  );

  const footer = !isLastStep && (
    <View style={[styles.footer, { borderTopColor: surface.border }]}>
      {step > 0 ? (
        <Pressable onPress={() => goTo(step - 1)} style={({ hovered }) => [styles.backBtn, hovered && { backgroundColor: surface.subtle }]}>
          <Icon name="arrow-left" size={12} color={colors.text_color_secondary} />
          <Text size="s" color="secondary" weight="medium">Back</Text>
        </Pressable>
      ) : <View />}
      <Pressable
        onPress={handleContinue}
        style={({ hovered, pressed }) => [
          styles.primaryBtn,
          styles.continueBtn,
          isMobile && { flex: 1, minWidth: 0 },
          { backgroundColor: hovered ? BRAND_GREEN_HOVER : BRAND_GREEN },
          pressed && { opacity: 0.9 },
        ]}
      >
        <Text size="s" weight="semibold" style={{ color: '#fff' }}>
          {step === 2 ? "Show me what's next" : 'Continue'}
        </Text>
        <Icon name="arrow-right" size={13} color="#fff" />
      </Pressable>
    </View>
  );

  const body = (
    <ScrollView
      ref={scrollRef}
      style={{ flex: 1 }}
      contentContainerStyle={[styles.scrollContent, { paddingHorizontal: isXS ? 16 : 24 }]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      <Animated.View style={{ opacity: fade, transform: [{ translateY: slide }] }}>
        {renderStep()}
      </Animated.View>
    </ScrollView>
  );

  if (isLastStep && renderNativeFinish && !isPlus) return renderNativeFinish();

  if (!isMobile) {
    return (
      <View style={[StyleSheet.absoluteFill, styles.desktopOverlay]}>
        <View style={[styles.desktopModal, {
          backgroundColor: colors.solid_color_primary,
          borderColor: colors.border_color_secondary,
          width: STEPS[step].desktopWidth,
        }]}>
          {header}
          {body}
          {footer}
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={[StyleSheet.absoluteFill, styles.mobileOverlay, { backgroundColor: colors.solid_color_primary, paddingBottom: insets.bottom }]}>
      <View style={{ paddingTop: Platform.OS === 'web' ? 12 : insets.top + 8 }}>{header}</View>
      {body}
      {footer && (
        <View style={{ backgroundColor: colors.solid_color_primary, paddingBottom: 20 }}>{footer}</View>
      )}
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  desktopOverlay: {
    zIndex: 999999,
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { backdropFilter: 'blur(6px)' } : {}),
  },
  desktopModal: {
    maxWidth: '94%',
    maxHeight: '92%',
    borderRadius: 22,
    borderWidth: 1,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 30px 70px rgba(0, 0, 0, 0.40)',
      transition: 'width 0.32s cubic-bezier(0.22, 1, 0.36, 1)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 24 },
      shadowOpacity: 0.35,
      shadowRadius: 48,
      elevation: 14,
    }),
  },
  mobileOverlay: { zIndex: 999999 },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 4,
    gap: 16,
  },
  progress: { flexDirection: 'row', gap: 6, flex: 1, maxWidth: 220 },
  progressSegment: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    ...(Platform.OS === 'web' ? { transition: 'background-color 0.3s ease' } : {}),
  },
  scrollContent: { flexGrow: 1, paddingTop: 18, paddingBottom: 22 },
  centered: { alignItems: 'center', width: '100%' },

  logoRow: { flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 14 },
  logoText: { fontSize: 24, letterSpacing: -0.5 },
  heroTitle: { fontSize: 28, lineHeight: 34, textAlign: 'center', letterSpacing: -0.6, marginBottom: 8 },
  heroSubtitle: { textAlign: 'center', lineHeight: 21, maxWidth: 400, marginBottom: 18 },
  teaserRow: { flexDirection: 'row', gap: 8, width: '100%', marginBottom: 18 },
  teaserPill: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    padding: 10,
    borderRadius: 12,
    borderWidth: 1,
  },
  teaserIcon: { width: 28, height: 28, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  nameSection: { width: '100%' },
  inlineNote: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 8 },

  sectionTitle: { fontSize: 23, lineHeight: 29, textAlign: 'center', letterSpacing: -0.5, marginBottom: 6 },
  sectionSubtitle: { textAlign: 'center', lineHeight: 20, marginBottom: 18, maxWidth: 520 },
  fieldBlock: { width: '100%', marginBottom: 18 },
  fieldLabel: { letterSpacing: 0.9, marginBottom: 10 },
  chipGrid: { flexDirection: 'row', flexWrap: 'wrap' },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 11,
    paddingVertical: 7,
    borderRadius: 9,
    borderWidth: 1,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.15s ease' } : {}),
  },
  goalGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'center' },
  goalTile: {
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
    gap: 7,
    position: 'relative',
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.15s ease' } : {}),
  },
  goalCheck: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 15,
    height: 15,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  goalIcon: { width: 32, height: 32, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },

  modeRow: { flexDirection: 'row', gap: 12, width: '100%', alignItems: 'stretch' },
  modeColumn: { flexDirection: 'column' },
  ruleStrip: {
    width: '100%',
    marginTop: 12,
    borderRadius: 12,
    borderWidth: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },

  plusCard: { width: '100%', borderRadius: 16, borderWidth: 1, padding: 14, marginBottom: 16 },
  plusHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  plusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 6,
    alignSelf: 'flex-start',
  },
  exampleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    borderRadius: 11,
    borderWidth: 1,
    ...(Platform.OS === 'web' ? { transition: 'all 0.15s ease' } : {}),
  },
  exampleIcon: { width: 28, height: 28, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  perkRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  perkItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  priceRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, gap: 10, flexWrap: 'wrap' },
  billingToggle: { flexDirection: 'row', borderRadius: 8, padding: 3, gap: 2 },
  billingOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 11,
    paddingVertical: 6,
    borderRadius: 6,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.15s ease' } : {}),
  },
  saveBadge: { backgroundColor: '#10B981', paddingHorizontal: 4, paddingVertical: 1, borderRadius: 4 },

  dividerRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 },
  dividerLine: { flex: 1, height: 1 },
  starterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 10,
    borderRadius: 11,
    borderWidth: 1,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'all 0.15s ease' } : {}),
  },
  tryChatCard: { width: '100%', borderRadius: 14, borderWidth: 1, padding: 14, gap: 12 },
  bonusStrip: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    borderRadius: 10, borderWidth: 1, paddingHorizontal: 10, paddingVertical: 8,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'background-color 0.15s ease' } : {}),
  },
  bonusBadge: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 7, paddingVertical: 4, borderRadius: 999, backgroundColor: '#10B981',
  },
  tryChatHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 40,
    borderRadius: 11,
    borderWidth: 1,
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'background-color 0.15s ease' } : {}),
  },
  ghostBtn: { marginTop: 12, paddingVertical: 8, paddingHorizontal: 14, borderRadius: 8, ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}) },

  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: 44,
    borderRadius: 12,
    width: '100%',
    ...(Platform.OS === 'web' ? { cursor: 'pointer', transition: 'background-color 0.15s ease' } : {}),
  },
  continueBtn: { width: 'auto', height: 40, paddingHorizontal: 20, alignSelf: 'flex-end' },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    gap: 12,
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingHorizontal: 12,
    paddingVertical: 9,
    borderRadius: 10,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
});
